import { useCallback, useMemo } from 'react';

import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { ReportSection } from 'apis/ReportApi';
import { StudentApi } from 'apis/StudentApi';
import { Card } from 'react-bootstrap';
import { useSuperState } from 'hooks';

import 'pages/reports/components/Report75/Report75.scss';

// NAJA courses (and only these) allow the expiration date of the SCA record to be edited.
const NAJA_CATEGORY_ID = 55;

interface Report75Props {
    onRefresh?: () => Promise<void> | void;
    reportSections: ReportSection[];
}

type Report75Row = Record<string, unknown>;

export const Report75 = ({
    onRefresh,
    reportSections
}: Report75Props) => {

    const { createToast } = useNotification();
    const { formatMessage } = useIntl();

    const [state, setState] = useSuperState({
        editRow: null as Report75Row | null,
        newExpireDate: '',
        saving: false
    });
    const { editRow, newExpireDate, saving } = state;

    // Report 75 renders as a single clean detail table (styled like report 68),
    // so pull the first table-shaped component out of whatever sections are returned.
    const tableComponent = reportSections
        .flatMap(section => section.Components ?? [])
        .find(component => component.Results?.Columns && component.Results?.Data);

    // Display CourseProgress as a rounded percentage and DateExpire without its time part.
    const formattedRowData = (tableComponent?.Results?.Data ?? []).map((row: Report75Row) => {

        const baseDate = typeof row.DateExpire === 'string' ? row.DateExpire.split(/[ T]/)[0] : row.DateExpire;

        // Coerce any type to a number, stripping formatting from strings; default to 0 when not numeric.
        const rawProgress = row.CourseProgress;
        const parsedProgress = typeof rawProgress === 'number'
            ? rawProgress
            : parseFloat(String(rawProgress ?? '').replace(/[^0-9.-]/g, ''));

        return {
            ...row,
            CourseProgress: `${Math.round(Number.isFinite(parsedProgress) ? parsedProgress : 0)}%`,
            DateExpire: baseDate
        };

    });

    const openEditModal = useCallback((row: Report75Row) => setState({
        editRow: row,
        newExpireDate: typeof row.DateExpire === 'string' ? row.DateExpire : ''
    }), []);

    const closeEditModal = () => setState({ editRow: null, newExpireDate: '', saving: false });

    const saveExpiration = async () => {

        if (!editRow || !newExpireDate) return;

        try {

            setState({ saving: true });
            const scaId = Number(editRow.StudentContentAccessId);
            const result = await StudentApi.UpdateNajaCourseExpiration(scaId, newExpireDate, Number(editRow.CategoryId));
            if (result?.Done) {

                createToast(result.Message || formatMessage({ id: 'saved.01bc8a02', defaultMessage: 'Saved' }), 'success');

                // Re-run the report so DateExpire and the derived bool columns (e.g. Active) reflect the saved value.
                await onRefresh?.();
                setState({ editRow: null, newExpireDate: '', saving: false });

            } else {

                setState({ saving: false });
                createToast(result?.Message || formatMessage({ id: 'unable_to_save.8c50fb55', defaultMessage: 'Unable to save' }), 'danger');

            }

        } catch (e) {

            setState({ saving: false });
            createToast(formatMessage({ id: 'unable_to_save.8c50fb55', defaultMessage: 'Unable to save' }), 'danger');

        }

    };

    // Custom DateExpire cell adds an inline edit pencil for NAJA (category 55) rows only.
    const DateExpireCell = useMemo(() => {

        const Cell = ({ dataItem }: { dataItem: Report75Row }) => {

            const canEdit = Number(dataItem?.CategoryId) === NAJA_CATEGORY_ID;

            return (
                <td className="gmetrixTableColumn__table-cell--no-wrap" role="gridcell">
                    <span className="d-inline-flex align-items-center">
                        {String(dataItem?.DateExpire ?? '')}
                        {canEdit && (
                            <i
                                className="fa-solid fa-pencil text-primary ms-2"
                                onClick={() => openEditModal(dataItem)}
                                role="button"
                                style={{ cursor: 'pointer' }}
                                title={formatMessage({ id: 'edit.00978daf', defaultMessage: 'Edit' })}
                            />
                        )}
                    </span>
                </td>
            );

        };

        return Cell;

    }, [openEditModal, formatMessage]);

    const columnData = (tableComponent?.Results?.Columns ?? [])
        .filter((col: { dataType?: string }) => col.dataType !== 'Hidden')
        .map((col: any) => (col.field === 'DateExpire' ? { ...col, cell: DateExpireCell } : col));

    return (
        <Card className="report75 mt-4">
            <Card.Body>
                {tableComponent ? (
                    <GMetrixTable
                        allowExport
                        columnData={columnData}
                        excelExportFileName={tableComponent.Title || 'Report75'}
                        exportContext="Report"
                        lockColumns={true}
                        rowData={formattedRowData}
                        style={{ fontSize: '18px', maxWidth: '100%', overflowX: 'auto' }}
                    />
                ) : (
                    <div className="text-center p-4">
                        <FormattedMessage defaultMessage="No Results Found" id="no_results_found.f1d71249" />
                    </div>
                )}

                <GMetrixDialog
                    actionButtonClick={saveExpiration}
                    actionButtonDisabled={saving || !newExpireDate}
                    actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
                    dialogLabel={formatMessage({ id: 'edit_expiration_date.5278eec4', defaultMessage: 'Edit Expiration Date' })}
                    onCancel={closeEditModal}
                    onClose={closeEditModal}
                    show={editRow !== null}
                >
                    <GMetrixDatePicker
                        label={formatMessage({ id: 'expiration_date.7e4bebd2', defaultMessage: 'Expiration Date' })}
                        onChange={(date: any) => setState({ newExpireDate: date ? date.toISOString() : '' })}
                        value={newExpireDate}
                    />
                </GMetrixDialog>
            </Card.Body>
        </Card>
    );

};
